import * as React from "react";
import { ControlProps } from "react-select/lib/components/Control";
import { MenuListComponentProps } from "react-select/lib/components/Menu";
import { Actions as ActionsBase } from "@common/components/actions";
import { ClickAwayListener } from "@common/components/ClickAwayListener";
import { Flex } from "@common/components/Flex";
import { DatasheetEditorHeaderProps } from "./header.types";
import { useController } from "./header.controller";
import { Button } from "@common/components/Button";
import { MessageBox } from "@common/components/MessageBox";
import Dropdown, { components, DROPDOWN_NO_VALUE } from "@common/containers/fields/Dropdown";

import { useDatasheetFormatManagementController } from "./format-management.controller";
import { FormatFieldSelector } from "@common/components/format-field-selector";
import { FormatColumnKey } from "@common/components/format-field-selector/components/formats/formats.types";
import { FolderPrimaryListDto } from "@common/dto/folder/primary-list-dto";
import { DATASHEETS_MODULE_NAME } from "@modules/datasheets/constants/common";
import { trDts } from "@modules/datasheets/utils/i18n";

import "./header.styles.scss";

export const Header: React.FC<DatasheetEditorHeaderProps> = (props) => {
  const {
    handleUpnChange,
    upns,
    actions,
    messageProps,
    closeMessage,
    showErrorMessage,
    datasheetFormatsManagementOpened,
    getMenuPortalStyles,
    handleDatasheetFormatsManagementOpen,
    handleDatasheetFormatsManagementClose,
    handleFormatAsTheDefaultDropdownClose,
    handleFormatAsTheDefaultDropdownChange,
  } = useController(props);
  const { upn, getFoldersForDatasheet, getDatasheetFolderFormats } = props;

  const {} = props;

  const {
    folders,
    folderFormats,
    isFoldersLoading,
    selectedFolder,
    selectedFormat,
    handleSelectedFolderChange,
    handleFormatSelect,
    isFolderFormatsLoading,
  } = useDatasheetFormatManagementController({
    getFolders: getFoldersForDatasheet,
    getFormats: getDatasheetFolderFormats,
    onSubmit: () => {
      handleDatasheetFormatsManagementClose();
    },
    showErrorMessage,
  });

  const getMenuStyles = React.useCallback(
    (css: React.CSSProperties) => ({
      ...css,
      width: undefined,
      left: "unset",
      right: 0,
    }),
    []
  );

  const getCurrentFormatSelectMenuListComponent = React.useCallback(
    (props: MenuListComponentProps<{}>) => {
      return (
        <components.MenuList
          {...props}
          className="DatasheetsResultHeader__formats__menu__set-current-format-dropdown__menu"
        >
          <h6 className="DatasheetsResultHeader__formats__menu__set-current-format-dropdown__title">
            {trDts("Recently Used Datasheet Formats")}
          </h6>

          {/* TODO: Update showing buttons condition */}
          {selectedFormat?.id &&
            [selectedFormat].map((format) => (
              <div key={format.id}>
                <Button
                  onClick={() => handleFormatAsTheDefaultDropdownChange(format)}
                  className="DatasheetsResultHeader__formats__menu__set-current-format-dropdown__action-btn"
                >
                  {format.name}
                </Button>
              </div>
            ))}
          <div className="DatasheetsResultHeader__formats__menu__set-current-format-dropdown__action-divider" />

          {/* TODO: Update showing button condition */}
          {selectedFormat?.id && (
            <Button
              onClick={() => handleFormatAsTheDefaultDropdownChange(selectedFormat)}
              className="DatasheetsResultHeader__formats__menu__set-current-format-dropdown__action-btn"
            >
              {trDts("Set Current Format as the Default for this Folder")}
            </Button>
          )}
        </components.MenuList>
      );
    },
    [selectedFormat?.id, handleFormatAsTheDefaultDropdownChange]
  );

  const getMenuListComponent = (props: MenuListComponentProps<{}>) => {
    return (
      <components.MenuList {...props} className="DatasheetsResultHeader__formats__menu">
        <ClickAwayListener onClickAway={handleFormatAsTheDefaultDropdownClose}>
          <Dropdown
            className="DatasheetsResultHeader__formats__menu__set-current-format-dropdown"
            fullWidth
            searchable={false}
            selectedValue={selectedFormat?.name || DROPDOWN_NO_VALUE}
            menuListComponent={getCurrentFormatSelectMenuListComponent}
            onBlur={handleFormatAsTheDefaultDropdownClose}
            onMenuClose={handleFormatAsTheDefaultDropdownClose}
          />
        </ClickAwayListener>
        <FormatFieldSelector
          folders={folders as Array<FolderPrimaryListDto>}
          selectedFolder={selectedFolder as FolderPrimaryListDto}
          isFoldersLoading={isFoldersLoading}
          selectedFormat={selectedFormat}
          onSelectedFolderChange={handleSelectedFolderChange}
          formats={folderFormats}
          isFormatsLoading={isFolderFormatsLoading}
          onSelectedFormatChange={handleFormatSelect}
          moduleName={DATASHEETS_MODULE_NAME.result}
          showTableHeader={false}
          classes={{
            folders: "DatasheetsResultHeader__format-management__folders",
            formats: "DatasheetsResultHeader__format-management__formats",
          }}
          tableColumns={[
            {
              label: trDts("Format Name"),
              field: FormatColumnKey.name,
            },
          ]}
        />
      </components.MenuList>
    );
  };

  const getControlComponent = React.useCallback(
    ({ cx, children, ...rest }: ControlProps<{}>) => (
      <components.Control cx={() => {}} {...rest} className="DatasheetsResultHeader__formats__menu-control">
        <Button
          variant="text"
          className="DatasheetsResultHeader__formats-btn"
          onClick={
            datasheetFormatsManagementOpened
              ? handleDatasheetFormatsManagementClose
              : handleDatasheetFormatsManagementOpen
          }
        >
          <i className="fa fa-edit" />
        </Button>
      </components.Control>
    ),
    [datasheetFormatsManagementOpened, handleDatasheetFormatsManagementClose, handleDatasheetFormatsManagementOpen]
  );

  return (
    <>
      <MessageBox hideMessageBox={closeMessage} {...messageProps} />
      <Flex className="DatasheetsResultHeader" alignItems="center" justify="space-between">
        <ActionsBase classes={{ item: "DatasheetsResultHeader__item" }} items={actions} />
        <Flex alignItems="center">
          <Flex alignItems="center">
            <span>UPN</span>
            <Dropdown
              placeholder=""
              className="DatasheetsResultHeader__upn-dropdown"
              id="btn-bootstrap-list-dropdown-drop"
              value={upn ?? DROPDOWN_NO_VALUE}
              options={upns}
              onValueChanged={handleUpnChange}
            />
          </Flex>
          <ClickAwayListener onClickAway={handleDatasheetFormatsManagementClose}>
            <Dropdown
              className="DatasheetsResultHeader__formats-dropdown"
              fullWidth
              searchable={false}
              menuIsOpen={datasheetFormatsManagementOpened}
              menuListComponent={getMenuListComponent}
              controlComponent={getControlComponent}
              styles={{
                width: "100%",
                menu: getMenuStyles,
                menuPortal: getMenuPortalStyles,
              }}
              onBlur={handleDatasheetFormatsManagementClose}
              onMenuClose={handleDatasheetFormatsManagementClose}
            />
          </ClickAwayListener>
        </Flex>
      </Flex>
    </>
  );
};
